import { Settings2, Loader2, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Agent } from '../../services/agents.service';
import { getAgentDisplayFields, getPlanLabel } from '../../utils/agent-i18n';
import { Tag } from '../ui/atoms/Tag';
import type { TagIntent, TagVariant } from '../ui/atoms/Tag';
import { Button } from '../ui/atoms/Button';

interface AgentCardProps {
  agent: Agent;
  onToggle: (enabled: boolean) => void;
  isPending: boolean;
}

const PLAN_TAG: Record<Agent['plan'], { intent: TagIntent; variant: TagVariant }> = {
  starter: { intent: 'info', variant: 'subtle' },
  core: { intent: 'primary', variant: 'subtle' },
  pro: { intent: 'secondary', variant: 'solid' },
};

/**
 * Uniform agent card — an oblique tinted wedge in the top-right, the agent's
 * name (as the big uppercase identity text), role and tagline at the
 * bottom-right, plan badge + action in the footer.
 */
export const AgentCard = ({ agent, onToggle, isPending }: AgentCardProps) => {
  const { t } = useTranslation('agents');
  const isSoon = agent.status === 'coming_soon';
  const { name, role, tagline } = getAgentDisplayFields(agent, t);
  const planLabel = getPlanLabel(agent.plan, t);
  const planTag = PLAN_TAG[agent.plan];

  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border border-light-lila/60 bg-light">
      <div className="relative flex min-h-[160px] flex-col">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(circle, rgba(23,29,39,0.07) 1px, transparent 1px)',
            backgroundSize: '20px 20px',
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -left-7 -top-7 h-28 w-28 rounded-full bg-purple-soft/70 blur-2xl"
        />
        <div className="relative mt-auto px-4 pb-4 pt-6 text-right">
          <h5 className="font-heading text-lg font-extrabold uppercase leading-tight tracking-tight text-dark-invirtus xl:text-xl">
            {name}
          </h5>
          <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-purple font-body">
            {role}
          </p>
          <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-dark-invirtus/55 font-body">
            {tagline}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-light-lila/50 px-4 py-3">
        <Tag
          intent={planTag.intent}
          variant={planTag.variant}
          className="uppercase"
        >
          {planLabel}
        </Tag>

        {isSoon ? (
          <Tag intent="secondary" variant="subtle" className="uppercase">
            {t('card.soon')}
          </Tag>
        ) : agent.locked ? (
          <Link
            to="/billing"
            className="inline-flex items-center gap-1.5 rounded-lg border border-light-lila px-2.5 py-1.5 text-xs font-semibold text-dark-invirtus transition-colors hover:bg-dark-invirtus-soft font-body"
          >
            <Lock className="h-3.5 w-3.5" />
            {t('card.upgrade_to', { plan: planLabel })}
          </Link>
        ) : (
          <div className="flex items-center gap-2">
            {agent.configurable && (
              <Button
                type="outlined"
                intent="secondary"
                size="xs"
                icon={<Settings2 />}
                label={t('card.configure')}
                onClick={() => toast(t('card.config_coming_soon', { name }))}
              />
            )}
            <button
              type="button"
              role="switch"
              aria-checked={agent.enabled}
              aria-label={
                agent.enabled
                  ? t('card.release_aria', { name })
                  : t('card.hire_aria', { name })
              }
              disabled={isPending}
              onClick={() => onToggle(!agent.enabled)}
              className={`relative h-5 w-9 shrink-0 rounded-full transition-colors disabled:opacity-60 ${
                agent.enabled ? 'bg-purple' : 'bg-light-lila'
              }`}
            >
              <span
                className={`absolute top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-light shadow-sm transition-transform ${
                  agent.enabled ? 'translate-x-[18px]' : 'translate-x-0.5'
                }`}
              >
                {isPending && (
                  <Loader2 className="h-2.5 w-2.5 animate-spin text-dark-invirtus/50" />
                )}
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
